// src/com/dc_temp_info/DcTempInfoInput.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button, DialogContent, DialogActions, Grid, CircularProgress, Slide, Slider } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune'; // 설정화면.
import { AppWord, AppObject, AppStyle } from '../../value/AppConst';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import DraggableEditor from '../../common/DraggableEditor';
import VzmNumberField from '../../common/VzmNumberField';
import VzmRadioForBool from '../../common/VzmRadioForBool';
import VzmSelector from '../../common/VzmSelector';
import { ErrorHintBox } from '../../common/VzmStyled';
import { useReactiveVar } from '@apollo/client';
import InputUtil from '../../value/InputUtil';
import { DcTempInfoDictionary } from './DcTempInfoDict';
import { userInfoRepo } from '../../value/AppModel';
import RadioSelector from '../../common/RadioSelector';

const TemperatureLineBox = styled(Box)({
    display: 'flex', alignItems: 'center', paddingLeft: 5, maxWidth: 450,
});
const LabelSpan = styled('span')({fontSize: '0.8rem', color:AppStyle.PrimaryRGB});
const TempSpan = styled('div')({fontSize: '0.9rem', textAlign: 'center'});

const InputMap = DcTempInfoDictionary.inputMap;

const defaultTemp = {
    tempSet1: -18,
    tempSet2St: -1,
    tempSet2En: 6,
    tempSet3St: 1,
    tempSet3En: 10,
    tempSet4St: 5,
    tempSet4En: 17,
};

function valuetext(value) {
    return `${value}°C`;
}

/**
 * (DC_TEMP_INFO) 정보 생성 또는 편집 Dialog
 * DC 별 기준온도 설정
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-12-06 10:10:36 using DbdbTap
 * https://www.dbdbtap.com
 */
export default function DcTempInfoInput({
    open,
    item,
    onClose,
    onRequestSave,
    responseSaving, // Response if result is NOT OK.
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [setTwoValues, setSetTwoValues] = useState([defaultTemp.tempSet2St, defaultTemp.tempSet2En]);
    const [setThreeValues, setSetThreeValues] = useState([defaultTemp.tempSet3St, defaultTemp.tempSet3En]);
    const [setFourValues, setSetFourValues] = useState([defaultTemp.tempSet4St, defaultTemp.tempSet4En]);
    const openConfirmDialog = useConfirmDialog();
    const {loading:loadingSave} = responseSaving; // 전송 결과에 대한 View 기능 소화.
    const isEdit = true; // 편의상. 실제 설정이 없던 경우라도 기본 데이터 틀이 item으로 들어오도록 되어 있음.

    // const dcTypeCdOptions = ValueMisc.makeCodesToSelectList(codeTree, CodeGroup.DC_TYPE_CD, InputMap.dcTypeCd.id);

    useEffect(()=>{
        if(open && item) {
            const inputSet = {...AppObject.emptyInputData};
            inputSet.data = {...item};
            if(item.tempYn==='Y') {
                setSetTwoValues([item.tempSet2St, item.tempSet2En]);
                setSetThreeValues([item.tempSet3St, item.tempSet3En]);
                setSetFourValues([item.tempSet4St, item.tempSet4En]);
            }
            else {
                Object.keys(defaultTemp).forEach((key) => {
                    if(item[key]===null || item[key]===undefined) inputSet.data[key] = defaultTemp[key];
                });
                if(!item.tempDeviation) inputSet.data.tempDeviation = 0;
                if(!item.tempGapMinute) inputSet.data.tempGapMinute = 30;
                if(!item.tempStartTime) inputSet.data.tempStartTime = 0;
            }
            setInput(inputSet);
        }
    }, [open, item]);

    const onChangeValue = (id, value, error) => {
        const v = id===InputMap.tempGapMinute.id ? Number(value) : value;
        const newInput = InputUtil.reflectData(input, id, v, error, InputMap, isEdit);
        setInput(newInput);
    };

    const onChangeRange = (id, value) => {
        const id2 = id.replace('St', 'En');
        if(id===InputMap.tempSet2St.id) setSetTwoValues(value);
        else if(id===InputMap.tempSet3St.id) setSetThreeValues(value);
        else if(id===InputMap.tempSet4St.id) setSetFourValues(value);

        const newInput = InputUtil.reflectData(input, id, value[0], false, InputMap, isEdit);
        const newInput2 = InputUtil.reflectData(newInput, id2, value[1], false, InputMap, isEdit);
        setInput(newInput2);
    };

    const onClickSubmit = () => {
        const data = {...input.data};
        if(data.smsYn !== 'Y') data.smsYn = 'N'; // make sure.
        const param = InputUtil.getDataToSubmit(data, InputMap, isEdit);
        onRequestSave(param, isEdit);
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    // Input field initiallized using getInputField(c,t,i)
    return(
        <DraggableEditor open={open} maxWidth='md'
            onClose={onClickCancel}
            startIcon={<TuneIcon fontSize={AppWord.LARGE}/>}
            title={sessionInfo.user.dscNameAs + ' 온도관제 설정'}
        >
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <VzmRadioForBool
                            dict={InputMap.tempYn}
                            value={input.data.tempYn}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <VzmNumberField
                            dict={InputMap.tempDeviation}
                            value={input.data.tempDeviation}
                            onChange={onChangeValue}
                            fullWidth={true}
                            disabled={input.data.tempYn !== 'Y'}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <VzmSelector
                            dict={InputMap.tempStartTime}
                            value={input.data.tempStartTime}
                            onChange={onChangeValue}
                            fullWidth={true}
                            disabled={input.data.tempYn !== 'Y'}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <RadioSelector
                            dict={InputMap.tempGapMinute}
                            value={input.data.tempGapMinute}
                            onChange={onChangeValue}
                            horizontal={true}
                            disabled={input.data.tempYn !== 'Y'}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <LabelSpan>기준온도 범위</LabelSpan>
                    </Grid>
                    <Grid item xs={1}>
                        &nbsp;
                    </Grid>
                    <Grid item xs={2}>
                        <LabelSpan>기준 -21°C</LabelSpan>
                    </Grid>
                    <Grid item xs={6}>
                        <TemperatureLineBox>
                            <Slider
                                aria-label="Custom marks"
                                value={input.data.tempSet1===null || input.data.tempSet1===undefined ? -18 : input.data.tempSet1}
                                getAriaValueText={valuetext}
                                step={1}
                                marks={[
                                    {value: -20, label: '-20°C'},
                                    {value: -15, label: '-15°C'},
                                    {value: -10, label: '-10°C'},
                                    {value: -5, label: '-5°C'},
                                    {value: 0, label: '0°C'},
                                    {value: 5, label: '5°C'},
                                ]}
                                color={AppStyle.mui.primary}
                                disabled={input.data.tempYn !== 'Y'}
                                onChange={(e,v)=>onChangeValue(InputMap.tempSet1.id, v)}
                                min={-20}
                                max={5}
                            />
                        </TemperatureLineBox>
                    </Grid>
                    <Grid item xs={3}>
                        <TempSpan>
                        {
                            input.data.tempSet1===null
                            || input.data.tempSet1===undefined
                            || input.data.tempYn !== 'Y'
                            ?
                            '-' : input.data.tempSet1 + '°C'
                        }
                        </TempSpan>
                    </Grid>
                    <Grid item xs={1}>
                        &nbsp;
                    </Grid>
                    <Grid item xs={2}>
                        <LabelSpan>기준 1°C</LabelSpan>
                    </Grid>
                    <Grid item xs={6}>
                        <TemperatureLineBox>
                            <Slider
                                getAriaLabel={() => 'Temperature range'}
                                value={setTwoValues}
                                getAriaValueText={valuetext}
                                step={1}
                                marks={[
                                    {value: -10, label: '-10°C'},
                                    {value: -5, label: '-5°C'},
                                    {value: 0, label: '0°C'},
                                    {value: 5, label: '5°C'},
                                    {value: 10, label: '10°C'},
                                    {value: 15, label: '15°C'},
                                ]}
                                disabled={input.data.tempYn !== 'Y'}
                                disableSwap={true}
                                onChange={(e,v)=>onChangeRange(InputMap.tempSet2St.id, v)}
                                min={-10}
                                max={15}
                                color={AppStyle.mui.success}
                            />
                        </TemperatureLineBox>
                    </Grid>
                    <Grid item xs={3}>
                        <TempSpan>
                        {
                            setTwoValues.length===0 || input.data.tempYn !== 'Y'
                            ?
                            '-' :
                            `${setTwoValues[0]}°C ~ ${setTwoValues[1]}°C`
                        }
                        </TempSpan>
                    </Grid>
                    <Grid item xs={1}>
                        &nbsp;
                    </Grid>
                    <Grid item xs={2}>
                        <LabelSpan>기준 5°C</LabelSpan>
                    </Grid>
                    <Grid item xs={6}>
                        <TemperatureLineBox>
                            <Slider
                                getAriaLabel={() => 'Temperature range'}
                                value={setThreeValues}
                                getAriaValueText={valuetext}
                                step={1}
                                marks={[
                                    {value: -5, label: '-5°C'},
                                    {value: 0, label: '0°C'},
                                    {value: 5, label: '5°C'},
                                    {value: 10, label: '10°C'},
                                    {value: 15, label: '15°C'},
                                    {value: 20, label: '20°C'},
                                ]}
                                disabled={input.data.tempYn !== 'Y'}
                                disableSwap={true}
                                onChange={(e,v)=>onChangeRange(InputMap.tempSet3St.id, v)}
                                min={-5}
                                max={20}
                                color={AppStyle.mui.secondary}
                            />
                        </TemperatureLineBox>
                    </Grid>
                    <Grid item xs={3}>
                        <TempSpan>
                        {
                            setThreeValues.length===0 || input.data.tempYn !== 'Y'
                            ?
                            '-' :
                            `${setThreeValues[0]}°C ~ ${setThreeValues[1]}°C`
                        }
                        </TempSpan>
                    </Grid>
                    <Grid item xs={1}>
                        &nbsp;
                    </Grid>
                    <Grid item xs={2}>
                        <LabelSpan>기준 10°C</LabelSpan>
                    </Grid>
                    <Grid item xs={6}>
                        <TemperatureLineBox>
                            <Slider
                                getAriaLabel={() => 'Temperature range'}
                                value={setFourValues}
                                getAriaValueText={valuetext}
                                step={1}
                                marks={[
                                    {value: 0, label: '0°C'},
                                    {value: 5, label: '5°C'},
                                    {value: 10, label: '10°C'},
                                    {value: 15, label: '15°C'},
                                    {value: 20, label: '20°C'},
                                    {value: 25, label: '25°C'},
                                ]}
                                disabled={input.data.tempYn !== 'Y'}
                                disableSwap={true}
                                onChange={(e,v)=>onChangeRange(InputMap.tempSet4St.id, v)}
                                min={0}
                                max={25}
                                color={AppStyle.mui.warning}
                            />
                        </TemperatureLineBox>
                    </Grid>
                    <Grid item xs={3}>
                        <TempSpan>
                        {
                            setFourValues.length===0 || input.data.tempYn !== 'Y'
                            ?
                            '-' :
                            `${setFourValues[0]}°C ~ ${setFourValues[1]}°C`
                        }
                        </TempSpan>
                    </Grid>
                    {/* <Grid item xs={12}>
                        <VzmRadioForBool
                            dict={InputMap.smsYn}
                            value={input.data.smsYn}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <VzmTextField
                            dict={InputMap.rcvrTelno1}
                            value={input.data.rcvrTelno1}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <VzmTextField
                            dict={InputMap.rcvrTelno2}
                            value={input.data.rcvrTelno2}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <VzmTextField
                            dict={InputMap.rcvrTelno3}
                            value={input.data.rcvrTelno3}
                            onChange={onChangeValue}
                            fullWidth={true}
                        />
                    </Grid> */}
                </Grid>
            </DialogContent>
            <DialogActions style={{display:'flex'}}>
                {
                    input.errorLabels && input.errorLabels.length > 0
                    ?
                    <ErrorHintBox>
                        확인: {input.errorLabels.join(', ')}
                    </ErrorHintBox>
                    : null
                }
                {
                    loadingSave
                    ?
                    <span>
                        <CircularProgress />
                        &nbsp;데이터 저장 중...
                    </span>
                    :
                    <>
                        <Button
                            onClick={onClickSubmit}
                            disabled={input.hasError || !input.changed}
                            variant={AppStyle.mui.contained}
                            color={AppStyle.mui.primary}
                        >저장</Button>
                        <Button onClick={onClickCancel}
                            color={AppStyle.mui.warning}
                            variant={AppStyle.mui.contained}
                        >취소</Button>
                    </>
                }
            </DialogActions>
        </DraggableEditor>
    );
}
